import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  Upload,
  FileText,
  Trash2,
  Download,
  Play,
  CheckCircle2,
  AlertCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileDown,
  Loader2,
  Eye,
  Files,
  Search,
  Filter,
  X,
  StickyNote,
  Zap,
  FileImage,
  RefreshCw,
  Tag,
  Plus
} from "lucide-react";
import { toast } from "sonner";

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const { api, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const batchFileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("federal");
  const [activeTab, setActiveTab] = useState("overview");
  const [extractingOcr, setExtractingOcr] = useState({});
  const [selectedTaxYear, setSelectedTaxYear] = useState(null);
  const [uploadDocYear, setUploadDocYear] = useState(null);
  
  // Enhancement states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterYear, setFilterYear] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [isDragging, setIsDragging] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Tag editing state
  const [editingTags, setEditingTags] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedDocForNote, setSelectedDocForNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [runningBulkOcr, setRunningBulkOcr] = useState(false);

  // Version upload states
  const [versionUploadOpen, setVersionUploadOpen] = useState(false);
  const [selectedDocForVersion, setSelectedDocForVersion] = useState(null);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const versionFileInputRef = useRef(null);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Year comparison states
  const [yearComparisonOpen, setYearComparisonOpen] = useState(false);
  const [comparisonCurrentYear, setComparisonCurrentYear] = useState(null);
  const [comparisonPriorYear, setComparisonPriorYear] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);

  // Saved reports states
  const [savedReports, setSavedReports] = useState([]);
  const [saveReportDialogOpen, setSaveReportDialogOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [savingReport, setSavingReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(null);

  // Generate tax years for dropdown
  const currentYear = new Date().getFullYear();
  const taxYears = Array.from({ length: 6 }, (_, i) => currentYear - i);
  
  // Get years that have documents uploaded
  const documentYears = [...new Set(documents.map(d => d.tax_year).filter(Boolean))].sort((a, b) => b - a);

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const [clientRes, docsRes, analysisRes, reportsRes] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/documents`),
        api.get(`/clients/${clientId}/analysis`),
        api.get(`/clients/${clientId}/reports`).catch(() => ({ data: [] })),
      ]);
      setClient(clientRes.data);
      setDocuments(docsRes.data);
      setSavedReports(reportsRes.data || []);
      
      // Set selected tax year from the most recent year with uploaded documents
      const docYears = [...new Set(docsRes.data.map(d => d.tax_year).filter(Boolean))].sort((a, b) => b - a);
      const defaultYear = docYears.length > 0 ? docYears[0] : (clientRes.data?.tax_year || currentYear - 1);
      setSelectedTaxYear(defaultYear);
      setUploadDocYear(clientRes.data?.tax_year || currentYear - 1); // Default upload year
      setAnalysis(analysisRes.data);
    } catch (error) {
      console.error("Error fetching client data:", error);
      toast.error("Failed to load client data");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!uploadDocYear) {
      toast.error("Please select a tax year for this document");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const response = await api.post(
        `/clients/${clientId}/documents?document_type=${selectedDocType}&tax_year=${uploadDocYear}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setDocuments([response.data, ...documents]);
      toast.success("Document uploaded successfully");
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Batch upload handler
  const handleBatchUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!uploadDocYear) {
      toast.error("Please select a tax year for these documents");
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    formData.append("document_type", selectedDocType);
    formData.append("tax_year", uploadDocYear.toString());

    setUploading(true);
    try {
      const response = await api.post(
        `/clients/${clientId}/documents/batch`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      if (response.data.uploaded?.length > 0) {
        setDocuments([...response.data.uploaded, ...documents]);
        toast.success(`${response.data.total_uploaded} document(s) uploaded successfully`);
      }
      
      if (response.data.failed?.length > 0) {
        toast.error(`${response.data.total_failed} document(s) failed to upload`);
      }
    } catch (error) {
      console.error("Error batch uploading:", error);
      toast.error(error.response?.data?.detail || "Failed to upload documents");
    } finally {
      setUploading(false);
      if (batchFileInputRef.current) {
        batchFileInputRef.current.value = "";
      }
    }
  };

  // OCR extraction handler
  const handleExtractOcr = async (documentId, force = false) => {
    setExtractingOcr(prev => ({ ...prev, [documentId]: true }));
    try {
      const response = await api.post(`/documents/${documentId}/ocr?force=${force}`);
      toast.success(`Extracted ${response.data.character_count} characters from document`);
      setDocuments(documents.map(d => 
        d.document_id === documentId 
          ? { ...d, ocr_processed: true, extracted_text: response.data.extracted_text, ocr_error: null } 
          : d
      ));
    } catch (error) {
      console.error("Error extracting OCR:", error);
      const errorMsg = error.response?.data?.detail || "Failed to extract text";
      toast.error(errorMsg);
      // Mark as failed OCR
      setDocuments(documents.map(d => 
        d.document_id === documentId 
          ? { ...d, ocr_processed: false, ocr_error: errorMsg } 
          : d
      ));
    } finally {
      setExtractingOcr(prev => ({ ...prev, [documentId]: false }));
    }
  };

  // Fixed: Download document with auth
  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Document downloaded");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openDeleteConfirm = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/documents/${documentToDelete.document_id}`);
      setDocuments(documents.filter(d => d.document_id !== documentToDelete.document_id));
      toast.success("Document deleted");
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  // === TAG MANAGEMENT ===
  const handleAddTag = async () => {
    if (!newTagInput.trim()) return;
    const newTag = newTagInput.trim();
    
    // Check if tag already exists
    if (client.tags?.includes(newTag)) {
      toast.error("Tag already exists");
      return;
    }
    
    const updatedTags = [...(client.tags || []), newTag];
    
    try {
      await api.put(`/clients/${clientId}`, { tags: updatedTags });
      setClient({ ...client, tags: updatedTags });
      setNewTagInput("");
      toast.success(`Tag "${newTag}" added`);
    } catch (error) {
      console.error("Error adding tag:", error);
      toast.error("Failed to add tag");
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    const updatedTags = (client.tags || []).filter(t => t !== tagToRemove);
    
    try {
      await api.put(`/clients/${clientId}`, { tags: updatedTags });
      setClient({ ...client, tags: updatedTags });
      toast.success(`Tag "${tagToRemove}" removed`);
    } catch (error) {
      console.error("Error removing tag:", error);
      toast.error("Failed to remove tag");
    }
  };

  // === ENHANCEMENT: Drag & Drop Upload ===
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!uploadDocYear) {
      toast.error("Please select a tax year before dropping files");
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => formData.append("files", file));
    formData.append("document_type", selectedDocType);
    formData.append("tax_year", uploadDocYear.toString());

    setUploading(true);
    try {
      const response = await api.post(
        `/clients/${clientId}/documents/batch`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      if (response.data.uploaded?.length > 0) {
        setDocuments([...response.data.uploaded, ...documents]);
        toast.success(`${response.data.total_uploaded} document(s) uploaded`);
      }
      if (response.data.failed?.length > 0) {
        toast.error(`${response.data.total_failed} failed: ${response.data.failed.map(f => f.error).join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [api, clientId, documents, selectedDocType, uploadDocYear]);

  // === ENHANCEMENT: Document Preview ===
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const handlePreviewDocument = async (doc) => {
    setPreviewLoading(true);
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      
      // Convert blob to data URL for better browser compatibility
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setPreviewDoc(doc);
        setPreviewLoading(false);
      };
      reader.onerror = () => {
        toast.error("Failed to load document preview");
        setPreviewLoading(false);
      };
      reader.readAsDataURL(response.data);
    } catch (error) {
      console.error("Error loading preview:", error);
      toast.error("Failed to load document preview");
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewDoc(null);
  };

  // === ENHANCEMENT: Document Notes ===
  const handleOpenNoteDialog = async (doc) => {
    setSelectedDocForNote(doc);
    setNoteText("");
    setNoteDialogOpen(true);
    
    // Fetch latest notes with history
    try {
      const response = await api.get(`/documents/${doc.document_id}/notes`);
      setSelectedDocForNote({ ...doc, ...response.data });
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedDocForNote || !noteText.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    setSavingNote(true);
    try {
      const response = await api.put(`/documents/${selectedDocForNote.document_id}/notes`, { notes: noteText });
      
      // Update documents list with new note info
      setDocuments(documents.map(d => 
        d.document_id === selectedDocForNote.document_id 
          ? { ...d, notes: noteText, last_note_by: response.data.note?.created_by_name, last_note_at: response.data.note?.created_at } 
          : d
      ));
      
      // Update selected doc with new note in history
      const updatedHistory = [...(selectedDocForNote.notes_history || []), response.data.note];
      setSelectedDocForNote(prev => ({
        ...prev,
        notes: noteText,
        notes_history: updatedHistory,
        last_note_by: response.data.note?.created_by_name,
        last_note_at: response.data.note?.created_at
      }));
      
      setNoteText("");
      toast.success("Note added");
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const formatNoteTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // === ENHANCEMENT: Bulk OCR ===
  const handleBulkOcr = async () => {
    const docsNeedingOcr = documents.filter(d => !d.ocr_processed);
    if (docsNeedingOcr.length === 0) {
      toast.info("All documents already have OCR processed");
      return;
    }

    setRunningBulkOcr(true);
    let successCount = 0;
    let failCount = 0;

    for (const doc of docsNeedingOcr) {
      try {
        await api.post(`/documents/${doc.document_id}/ocr`);
        successCount++;
        setDocuments(prev => prev.map(d => 
          d.document_id === doc.document_id ? { ...d, ocr_processed: true } : d
        ));
      } catch (error) {
        failCount++;
      }
    }

    setRunningBulkOcr(false);
    if (successCount > 0) toast.success(`OCR completed for ${successCount} document(s)`);
    if (failCount > 0) toast.error(`OCR failed for ${failCount} document(s)`);
  };

  // === DOCUMENT VERSIONING ===
  const openVersionUpload = (doc) => {
    setSelectedDocForVersion(doc);
    setVersionUploadOpen(true);
  };

  const handleVersionUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDocForVersion) return;

    setUploadingVersion(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/documents/${selectedDocForVersion.document_id}/version`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // Update the document in the list with new version info
      setDocuments(docs => docs.map(d => 
        d.document_id === selectedDocForVersion.document_id 
          ? { ...d, is_latest: false }
          : d
      ).concat([response.data]));

      toast.success(`Version ${response.data.version} uploaded successfully`);
      setVersionUploadOpen(false);
      setSelectedDocForVersion(null);
    } catch (error) {
      console.error("Error uploading version:", error);
      toast.error(error.response?.data?.detail || "Failed to upload new version");
    } finally {
      setUploadingVersion(false);
      if (versionFileInputRef.current) {
        versionFileInputRef.current.value = "";
      }
    }
  };

  const viewDocumentVersions = async (doc) => {
    setSelectedDocForVersion(doc);
    setLoadingVersions(true);
    setVersionsDialogOpen(true);
    
    try {
      const response = await api.get(`/documents/${doc.document_id}/versions`);
      setDocumentVersions(response.data);
    } catch (error) {
      console.error("Error loading versions:", error);
      toast.error("Failed to load document versions");
    } finally {
      setLoadingVersions(false);
    }
  };

  // === YEAR COMPARISON ===
  const handleYearComparison = async () => {
    if (!comparisonCurrentYear || !comparisonPriorYear) {
      toast.error("Please select both years for comparison");
      return;
    }

    if (comparisonCurrentYear === comparisonPriorYear) {
      toast.error("Please select different years for comparison");
      return;
    }

    setComparing(true);
    try {
      const response = await api.post(`/clients/${clientId}/compare-years`, {
        current_year: comparisonCurrentYear,
        prior_year: comparisonPriorYear
      });
      setComparisonResult(response.data);
      toast.success("Year comparison completed");
    } catch (error) {
      console.error("Error comparing years:", error);
      toast.error(error.response?.data?.detail || "Failed to compare years");
    } finally {
      setComparing(false);
    }
  };

  // Get unique years from documents for comparison dropdown
  const availableYears = [...new Set(documents.map(d => d.tax_year).filter(Boolean))].sort((a, b) => b - a);

  // === ENHANCEMENT: Duplicate Detection ===
  const checkForDuplicates = (filename, fileSize) => {
    return documents.find(d => 
      d.filename === filename && Math.abs(d.file_size - fileSize) < 100
    );
  };

  // === ENHANCEMENT: Filter documents ===
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchQuery || 
      doc.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesYear = filterYear === "all" || doc.tax_year?.toString() === filterYear;
    const matchesType = filterType === "all" || doc.document_type === filterType;
    return matchesSearch && matchesYear && matchesType;
  });

  const handleAnalyze = async () => {
    if (documents.length === 0) {
      toast.error("Please upload at least one document before analyzing");
      return;
    }
    
    // Check if there are documents for the selected year
    const docsForYear = documents.filter(d => d.tax_year === selectedTaxYear);
    if (docsForYear.length === 0) {
      toast.error(`No documents found for tax year ${selectedTaxYear}. Please select a year that has documents uploaded.`);
      return;
    }

    // Check if any documents have OCR processed
    const hasOcrDocs = docsForYear.some(d => d.ocr_processed || d.extracted_text);
    if (!hasOcrDocs) {
      toast.warning(`Please run OCR on your ${selectedTaxYear} documents first for accurate analysis`);
      return;
    }

    setAnalyzing(true);
    try {
      const response = await api.post(`/clients/${clientId}/analyze?tax_year=${selectedTaxYear}`);
      setAnalysis(response.data);
      setClient({ ...client, status: "completed" });
      setActiveTab("analysis");
      toast.success(`Analysis completed for tax year ${selectedTaxYear}`);
    } catch (error) {
      console.error("Error analyzing documents:", error);
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  // Download PDF report
  const handleDownloadReport = async () => {
    if (!analysis) return;
    
    try {
      toast.info("Generating PDF report...");
      // Pass the analysis tax year to get the correct report
      const response = await api.get(`/clients/${clientId}/report/pdf?tax_year=${analysis.tax_year}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${client?.name?.replace(/\s+/g, '_')}_Tax_Summary_${analysis?.tax_year || selectedTaxYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error(error.response?.data?.detail || "Failed to download PDF report");
    }
  };

  // Save report with custom name
  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      toast.error("Please enter a name for the report");
      return;
    }

    setSavingReport(true);
    try {
      const response = await api.post(`/clients/${clientId}/reports/save`, {
        name: reportName.trim(),
        notes: reportNotes.trim() || null
      });
      setSavedReports([response.data, ...savedReports]);
      toast.success("Report saved successfully!");
      setSaveReportDialogOpen(false);
      setReportName("");
      setReportNotes("");
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error(error.response?.data?.detail || "Failed to save report");
    } finally {
      setSavingReport(false);
    }
  };

  // Download saved report
  const handleDownloadSavedReport = async (report) => {
    setDownloadingReport(report.report_id);
    try {
      const response = await api.get(`/clients/${clientId}/reports/${report.report_id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.name.replace(/\s+/g, '_')}_${report.tax_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded!");
    } catch (error) {
      console.error("Error downloading saved report:", error);
      toast.error("Failed to download report");
    } finally {
      setDownloadingReport(null);
    }
  };

  // Delete saved report
  const handleDeleteSavedReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this saved report?")) return;
    
    try {
      await api.delete(`/clients/${clientId}/reports/${reportId}`);
      setSavedReports(savedReports.filter(r => r.report_id !== reportId));
      toast.success("Report deleted");
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="status-pending">Pending</Badge>;
      case "in_progress":
        return <Badge className="status-in-progress">In Progress</Badge>;
      case "completed":
        return <Badge className="status-completed">Completed</Badge>;
      case "analyzing":
        return <Badge className="status-in-progress">Analyzing</Badge>;
      case "error":
        return <Badge className="status-error">Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      federal: "Federal",
      state: "State",
      local: "Local",
      school_district: "School District",
      prior_year: "Prior Year",
      other: "Other",
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount) => {
    if (amount == null) return "N/A";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/clients")}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-to-clients"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="client-name">
                {client?.name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                {getStatusBadge(client?.status)}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Tax Year Selector - Only shows years with documents */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-500 font-body whitespace-nowrap">Tax Year:</Label>
              <Select 
                value={selectedTaxYear?.toString() || ""} 
                onValueChange={(v) => setSelectedTaxYear(parseInt(v))}
                disabled={documentYears.length === 0}
              >
                <SelectTrigger className="w-[100px] font-body rounded-md border-slate-200" data-testid="tax-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {documentYears.length > 0 ? (
                    documentYears.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No documents uploaded</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {documentYears.length === 0 && (
                <span className="text-xs text-amber-600">Upload documents first</span>
              )}
            </div>
            
            {analysis?.status === "completed" && (
              <Button 
                variant="outline"
                onClick={handleDownloadReport}
                className="font-body rounded-md border-slate-200"
                data-testid="download-report-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
            <Button 
              onClick={handleAnalyze}
              disabled={analyzing || documents.length === 0}
              className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md shadow-sm"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Analyze Documents
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-md" data-testid="client-tabs">
            <TabsTrigger value="overview" className="font-body rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Overview
            </TabsTrigger>
            <TabsTrigger value="documents" className="font-body rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Documents ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="analysis" className="font-body rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Tax Analysis
            </TabsTrigger>
            <TabsTrigger value="reports" className="font-body rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Saved Reports ({savedReports.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="client-info-card">
                <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                  <CardTitle className="text-lg font-heading font-semibold text-slate-900">Client Information</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label className="text-sm text-slate-500 font-body">Name</Label>
                    <p className="font-body font-medium text-slate-900">{client?.name}</p>
                  </div>
                  {client?.email && (
                    <div>
                      <Label className="text-sm text-slate-500 font-body">Email</Label>
                      <p className="font-body text-slate-900">{client.email}</p>
                    </div>
                  )}
                  {client?.phone && (
                    <div>
                      <Label className="text-sm text-slate-500 font-body">Phone</Label>
                      <p className="font-body text-slate-900">{client.phone}</p>
                    </div>
                  )}
                  {client?.address && (
                    <div>
                      <Label className="text-sm text-slate-500 font-body">Address</Label>
                      <p className="font-body text-slate-900">{client.address}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm text-slate-500 font-body">Tax Year</Label>
                    <p className="font-mono text-slate-900">{client?.tax_year}</p>
                  </div>
                  
                  {/* Tags Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm text-slate-500 font-body flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Tags
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setEditingTags(!editingTags)}
                      >
                        {editingTags ? "Done" : "Edit"}
                      </Button>
                    </div>
                    
                    {/* Display Tags */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {client?.tags?.length > 0 ? (
                        client.tags.map(tag => (
                          <Badge 
                            key={tag} 
                            variant="secondary" 
                            className="flex items-center gap-1 bg-slate-100"
                          >
                            {tag}
                            {editingTags && (
                              <X 
                                className="w-3 h-3 cursor-pointer hover:text-rose-500 transition-colors" 
                                onClick={() => handleRemoveTag(tag)}
                              />
                            )}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400 italic">No tags</span>
                      )}
                    </div>
                    
                    {/* Add Tag Input */}
                    {editingTags && (
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="Add a tag..."
                          value={newTagInput}
                          onChange={(e) => setNewTagInput(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
                          className="h-8 text-sm"
                          data-testid="add-tag-input"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 px-2"
                          onClick={handleAddTag}
                          data-testid="add-tag-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="quick-stats-card">
                <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                  <CardTitle className="text-lg font-heading font-semibold text-slate-900">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                    <span className="text-sm font-body text-slate-600">Documents Uploaded</span>
                    <span className="font-heading font-semibold text-slate-900">{documents.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                    <span className="text-sm font-body text-slate-600">Analysis Status</span>
                    {analysis ? getStatusBadge(analysis.status) : <Badge variant="outline">Not Started</Badge>}
                  </div>
                  {analysis?.federal_summary?.refund_or_owe != null && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                      <span className="text-sm font-body text-slate-600">Federal Refund/Owe</span>
                      <span className={`font-heading font-semibold ${analysis.federal_summary.refund_or_owe >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(analysis.federal_summary.refund_or_owe)}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {/* Upload Section with Drag & Drop */}
            <Card 
              ref={dropZoneRef}
              className={`bg-white border-2 shadow-sm rounded-sm transition-colors duration-200 ${
                isDragging ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="upload-section"
            >
              <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                <CardTitle className="text-lg font-heading font-semibold text-slate-900">Upload Documents</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Drag & Drop Zone */}
                {isDragging ? (
                  <div className="flex flex-col items-center justify-center py-12 text-emerald-600">
                    <Upload className="w-12 h-12 mb-4 animate-bounce" />
                    <p className="font-body font-medium">Drop files here to upload</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {/* Row 1: Tax Year and Document Type */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600 font-body whitespace-nowrap">Tax Year:</Label>
                        <Select value={uploadDocYear?.toString() || ""} onValueChange={(v) => setUploadDocYear(parseInt(v))}>
                          <SelectTrigger className="w-[100px] font-body rounded-md border-slate-200" data-testid="upload-tax-year-select">
                            <SelectValue placeholder="Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {taxYears.map(year => (
                              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                        <SelectTrigger className="w-full sm:w-[200px] font-body rounded-md" data-testid="doc-type-select">
                          <SelectValue placeholder="Document type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="federal">Federal Tax Return</SelectItem>
                          <SelectItem value="state">State Tax Return</SelectItem>
                          <SelectItem value="local">Local Tax Return</SelectItem>
                          <SelectItem value="school_district">School District</SelectItem>
                          <SelectItem value="prior_year">Prior Year</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Row 2: Upload Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                        data-testid="file-input"
                      />
                      <label htmlFor="file-upload">
                        <Button 
                          asChild
                          disabled={uploading}
                          className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md cursor-pointer"
                        >
                          <span>
                            {uploading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload File
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                      
                      <input
                        ref={batchFileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        multiple
                        onChange={handleBatchUpload}
                        className="hidden"
                        id="batch-file-upload"
                        data-testid="batch-file-input"
                      />
                      <label htmlFor="batch-file-upload">
                        <Button 
                          asChild
                          variant="outline"
                          disabled={uploading}
                          className="border-slate-200 font-body rounded-md cursor-pointer"
                        >
                          <span>
                            <Files className="w-4 h-4 mr-2" />
                            Batch Upload
                          </span>
                        </Button>
                    </label>
                      
                      {/* Bulk OCR Button */}
                      <Button 
                        variant="outline"
                        onClick={handleBulkOcr}
                        disabled={runningBulkOcr || documents.filter(d => !d.ocr_processed).length === 0}
                        className="border-slate-200 font-body rounded-md"
                      >
                        {runningBulkOcr ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Bulk OCR
                      </Button>
                    </div>
                    
                    <p className="text-xs text-slate-500 font-body">
                      Drag & drop files here, or use the buttons above. Supported: PDF, JPG, PNG
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Search & Filter Bar */}
            <Card className="bg-white border border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 font-body rounded-md border-slate-200"
                      data-testid="search-documents"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={filterYear} onValueChange={setFilterYear}>
                      <SelectTrigger className="w-[120px] font-body rounded-md border-slate-200">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Years</SelectItem>
                        {taxYears.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-[150px] font-body rounded-md border-slate-200">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="federal">Federal</SelectItem>
                        <SelectItem value="state">State</SelectItem>
                        <SelectItem value="local">Local</SelectItem>
                        <SelectItem value="school_district">School District</SelectItem>
                        <SelectItem value="prior_year">Prior Year</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documents List */}
            <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="documents-list">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading font-semibold text-slate-900">
                    Uploaded Documents ({filteredDocuments.length})
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredDocuments.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {filteredDocuments.map((doc) => (
                      <div 
                        key={doc.document_id}
                        className="p-4 hover:bg-slate-50/50 transition-colors duration-200"
                        data-testid={`document-${doc.document_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            {/* Tax Year Badge */}
                            <div className="w-14 h-14 bg-slate-900 rounded-sm flex flex-col items-center justify-center text-white flex-shrink-0">
                              <span className="text-xs font-body opacity-70">TAX</span>
                              <span className="text-lg font-heading font-bold">{doc.tax_year || '—'}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-body font-medium text-slate-900 truncate">{doc.filename}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge variant="outline" className="text-xs">{getDocTypeLabel(doc.document_type)}</Badge>
                                <span className="text-xs text-slate-500 font-body">
                                  {(doc.file_size / 1024).toFixed(1)} KB
                                </span>
                                {/* OCR Status - Show different states */}
                                {doc.ocr_processed && doc.extracted_text ? (
                                  <Badge 
                                    className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs cursor-pointer hover:bg-emerald-100"
                                    onClick={() => handleExtractOcr(doc.document_id, true)}
                                    title={`${doc.extracted_text.length} chars extracted. Click to re-run OCR.`}
                                  >
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    OCR ({Math.round(doc.extracted_text.length / 1000)}k)
                                  </Badge>
                                ) : doc.ocr_processed && !doc.extracted_text ? (
                                  <Badge 
                                    className="bg-amber-50 text-amber-700 border border-amber-200 text-xs cursor-pointer hover:bg-amber-100"
                                    onClick={() => handleExtractOcr(doc.document_id, true)}
                                    title="OCR ran but no text extracted. Click to retry."
                                  >
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    OCR Empty - Retry
                                  </Badge>
                                ) : doc.ocr_error ? (
                                  <Badge 
                                    className="bg-rose-50 text-rose-700 border border-rose-200 text-xs cursor-pointer hover:bg-rose-100"
                                    onClick={() => handleExtractOcr(doc.document_id, true)}
                                    title={doc.ocr_error}
                                  >
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    OCR Failed - Retry
                                  </Badge>
                                ) : null}
                                {doc.version > 1 && (
                                  <Badge 
                                    className="bg-blue-50 text-blue-700 border border-blue-200 text-xs cursor-pointer hover:bg-blue-100"
                                    onClick={() => viewDocumentVersions(doc)}
                                  >
                                    v{doc.version}
                                  </Badge>
                                )}
                                {doc.is_latest === false && (
                                  <Badge className="bg-slate-100 text-slate-500 border border-slate-200 text-xs">
                                    Older Version
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Notes Preview - Show if note exists */}
                              {doc.notes && (
                                <div 
                                  className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded-sm cursor-pointer hover:bg-amber-100 transition-colors"
                                  onClick={() => handleOpenNoteDialog(doc)}
                                >
                                  <div className="flex items-start gap-2">
                                    <StickyNote className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-amber-800 font-body line-clamp-2">{doc.notes}</p>
                                      {doc.last_note_by && (
                                        <p className="text-xs text-amber-600 font-body mt-1">
                                          — {doc.last_note_by}, {formatNoteTime(doc.last_note_at)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                            {/* Preview Button */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handlePreviewDocument(doc)}
                              className="text-slate-600 hover:text-slate-900"
                              title="Preview document"
                              disabled={previewLoading}
                              data-testid={`preview-doc-${doc.document_id}`}
                            >
                              {previewLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileImage className="w-4 h-4" />
                              )}
                            </Button>
                            
                            {/* Note Button */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOpenNoteDialog(doc)}
                              className={doc.notes ? "text-amber-600 hover:text-amber-700" : "text-slate-600 hover:text-slate-900"}
                              title="Add/view notes"
                            >
                              <StickyNote className="w-4 h-4" />
                            </Button>
                            
                            {/* OCR Button - Show if not processed or if processed but empty */}
                            {(!doc.ocr_processed || !doc.extracted_text) && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleExtractOcr(doc.document_id, true)}
                                disabled={extractingOcr[doc.document_id]}
                                className={doc.ocr_error || (doc.ocr_processed && !doc.extracted_text) 
                                  ? "text-amber-600 hover:text-amber-700" 
                                  : "text-slate-600 hover:text-slate-900"}
                                title={doc.ocr_error || (doc.ocr_processed && !doc.extracted_text) 
                                  ? "Retry OCR extraction" 
                                  : "Extract text using OCR"}
                              >
                                {extractingOcr[doc.document_id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            
                            {/* Download Button */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadDocument(doc)}
                              className="text-slate-600 hover:text-slate-900"
                              title="Download document"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            
                            {/* New Version Button */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openVersionUpload(doc)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Upload new version"
                              data-testid={`version-doc-${doc.document_id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          
                            {/* Delete Button */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openDeleteConfirm(doc)}
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              title="Delete document"
                              data-testid={`delete-doc-${doc.document_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Upload className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500 font-body">
                      {documents.length > 0 ? "No documents match your filters" : "No documents uploaded yet"}
                    </p>
                    <p className="text-sm text-slate-400 font-body mt-1">
                      {documents.length > 0 ? "Try adjusting your search or filters" : "Upload tax documents to begin analysis"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6">
            {analysis?.status === "completed" ? (
              <>
                {/* Download Report Banner */}
                <Card className="bg-emerald-50 border border-emerald-200 shadow-sm rounded-sm" data-testid="download-report-banner">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-body font-medium text-emerald-900">Analysis Complete - Tax Year {analysis.tax_year}</p>
                        <p className="text-sm text-emerald-700 font-body">Download the comprehensive report for your client</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setReportName(`${client?.name || 'Client'} - Tax Summary ${analysis?.tax_year || selectedTaxYear}`);
                          setSaveReportDialogOpen(true);
                        }}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-body rounded-md"
                        data-testid="save-report-btn"
                      >
                        <Files className="w-4 h-4 mr-2" />
                        Save Report
                      </Button>
                      <Button 
                        onClick={handleDownloadReport}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-body rounded-md"
                        data-testid="analysis-download-pdf-btn"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Federal Summary */}
                  {analysis.federal_summary && (
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="federal-summary-card">
                      <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                        <CardTitle className="text-base font-heading font-semibold text-slate-900">Federal</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500 font-body">AGI</span>
                          <span className="font-mono text-sm text-slate-900">{formatCurrency(analysis.federal_summary.adjusted_gross_income)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500 font-body">Total Tax</span>
                          <span className="font-mono text-sm text-slate-900">{formatCurrency(analysis.federal_summary.total_tax)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                          <span className="text-sm font-medium text-slate-700 font-body">Refund/Owe</span>
                          <span className={`font-mono text-sm font-semibold ${analysis.federal_summary.refund_or_owe >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(analysis.federal_summary.refund_or_owe)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* State Summary */}
                  {analysis.state_summary && (
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="state-summary-card">
                      <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                        <CardTitle className="text-base font-heading font-semibold text-slate-900">
                          State {analysis.state_summary.state && `(${analysis.state_summary.state})`}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500 font-body">Taxable Income</span>
                          <span className="font-mono text-sm text-slate-900">{formatCurrency(analysis.state_summary.taxable_income)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500 font-body">State Tax</span>
                          <span className="font-mono text-sm text-slate-900">{formatCurrency(analysis.state_summary.state_tax)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                          <span className="text-sm font-medium text-slate-700 font-body">Refund/Owe</span>
                          <span className={`font-mono text-sm font-semibold ${analysis.state_summary.refund_or_owe >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(analysis.state_summary.refund_or_owe)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Local Summary */}
                  {analysis.local_summary && (
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="local-summary-card">
                      <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                        <CardTitle className="text-base font-heading font-semibold text-slate-900">
                          Local {analysis.local_summary.locality && `(${analysis.local_summary.locality})`}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500 font-body">Local Tax</span>
                          <span className="font-mono text-sm text-slate-900">{formatCurrency(analysis.local_summary.local_tax)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                          <span className="text-sm font-medium text-slate-700 font-body">Refund/Owe</span>
                          <span className={`font-mono text-sm font-semibold ${analysis.local_summary.refund_or_owe >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(analysis.local_summary.refund_or_owe)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* School District Summary */}
                  {analysis.school_district_summary && (
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="school-summary-card">
                      <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                        <CardTitle className="text-base font-heading font-semibold text-slate-900">
                          School District {analysis.school_district_summary.district && `(${analysis.school_district_summary.district})`}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500 font-body">School Tax</span>
                          <span className="font-mono text-sm text-slate-900">{formatCurrency(analysis.school_district_summary.school_tax)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Prior Year Comparison */}
                {analysis.prior_year_comparison && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="prior-year-card">
                    <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                      <CardTitle className="text-lg font-heading font-semibold text-slate-900">Prior Year Comparison</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="flex items-center gap-2 mb-2">
                            {analysis.prior_year_comparison.income_change >= 0 ? (
                              <TrendingUp className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-rose-600" />
                            )}
                            <span className="text-sm text-slate-500 font-body">Income Change</span>
                          </div>
                          <p className={`text-2xl font-heading font-bold ${analysis.prior_year_comparison.income_change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(analysis.prior_year_comparison.income_change)}
                          </p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="flex items-center gap-2 mb-2">
                            {analysis.prior_year_comparison.tax_change <= 0 ? (
                              <TrendingDown className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <TrendingUp className="w-5 h-5 text-rose-600" />
                            )}
                            <span className="text-sm text-slate-500 font-body">Tax Change</span>
                          </div>
                          <p className={`text-2xl font-heading font-bold ${analysis.prior_year_comparison.tax_change <= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(analysis.prior_year_comparison.tax_change)}
                          </p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-sm">
                          <div className="flex items-center gap-2 mb-2">
                            {analysis.prior_year_comparison.refund_change >= 0 ? (
                              <TrendingUp className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-rose-600" />
                            )}
                            <span className="text-sm text-slate-500 font-body">Refund Change</span>
                          </div>
                          <p className={`text-2xl font-heading font-bold ${analysis.prior_year_comparison.refund_change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(analysis.prior_year_comparison.refund_change)}
                          </p>
                        </div>
                      </div>
                      {analysis.prior_year_comparison.key_differences?.length > 0 && (
                        <div className="mt-6">
                          <p className="text-sm font-medium text-slate-700 font-body mb-2">Key Differences:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {analysis.prior_year_comparison.key_differences.map((diff, i) => (
                              <li key={i} className="text-sm text-slate-600 font-body">{diff}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Refund/Owe Explanation */}
                {analysis.refund_owe_explanation && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="explanation-card">
                    <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                      <CardTitle className="text-lg font-heading font-semibold text-slate-900">Refund/Owe Explanation</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="text-slate-600 font-body whitespace-pre-wrap">{analysis.refund_owe_explanation}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Action Items */}
                {analysis.action_items?.length > 0 && (
                  <Card className="bg-amber-50 border border-amber-200 shadow-sm rounded-sm" data-testid="action-items-card">
                    <CardHeader className="border-b border-amber-100 bg-amber-100/50 p-4">
                      <CardTitle className="text-lg font-heading font-semibold text-amber-900 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Action Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <ul className="space-y-3">
                        {analysis.action_items.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-amber-200 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-heading font-semibold text-amber-800">{i + 1}</span>
                            </div>
                            <span className="text-amber-900 font-body">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Overall Summary */}
                {analysis.overall_summary && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="overall-summary-card">
                    <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4">
                      <CardTitle className="text-lg font-heading font-semibold text-slate-900">Overall Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="text-slate-600 font-body whitespace-pre-wrap">{analysis.overall_summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Year-to-Year Comparison Tool */}
                {availableYears.length >= 2 && (
                  <Card className="bg-blue-50 border border-blue-200 shadow-sm rounded-sm" data-testid="year-comparison-card">
                    <CardHeader className="border-b border-blue-100 bg-blue-100/50 p-4">
                      <CardTitle className="text-lg font-heading font-semibold text-blue-900 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Year-to-Year Comparison
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="text-sm text-blue-700 font-body mb-4">
                        Compare tax data between different years to identify trends and changes.
                      </p>
                      <div className="flex flex-wrap items-end gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-blue-800">Current Year</Label>
                          <Select value={comparisonCurrentYear?.toString() || ""} onValueChange={(v) => setComparisonCurrentYear(parseInt(v))}>
                            <SelectTrigger className="w-[140px] bg-white border-blue-200">
                              <SelectValue placeholder="Select year" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableYears.map(year => (
                                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-blue-800">Prior Year</Label>
                          <Select value={comparisonPriorYear?.toString() || ""} onValueChange={(v) => setComparisonPriorYear(parseInt(v))}>
                            <SelectTrigger className="w-[140px] bg-white border-blue-200">
                              <SelectValue placeholder="Select year" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableYears.map(year => (
                                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          onClick={handleYearComparison}
                          disabled={comparing || !comparisonCurrentYear || !comparisonPriorYear}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-body rounded-md"
                          data-testid="compare-years-btn"
                        >
                          {comparing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Comparing...
                            </>
                          ) : (
                            <>
                              <TrendingUp className="w-4 h-4 mr-2" />
                              Compare Years
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Comparison Results */}
                      {comparisonResult && (
                        <div className="mt-6 pt-6 border-t border-blue-200">
                          <h4 className="font-heading font-semibold text-blue-900 mb-4">
                            Comparison: {comparisonResult.current_year} vs {comparisonResult.prior_year}
                          </h4>
                          
                          {/* Income Comparison */}
                          {comparisonResult.income_comparison && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div className="p-3 bg-white rounded-sm border border-blue-100">
                                <p className="text-xs text-slate-500 font-body">Current Year Income</p>
                                <p className="text-lg font-heading font-bold text-slate-900">
                                  {formatCurrency(comparisonResult.income_comparison.current_year_income)}
                                </p>
                              </div>
                              <div className="p-3 bg-white rounded-sm border border-blue-100">
                                <p className="text-xs text-slate-500 font-body">Prior Year Income</p>
                                <p className="text-lg font-heading font-bold text-slate-900">
                                  {formatCurrency(comparisonResult.income_comparison.prior_year_income)}
                                </p>
                              </div>
                              <div className="p-3 bg-white rounded-sm border border-blue-100">
                                <p className="text-xs text-slate-500 font-body">Change</p>
                                <p className={`text-lg font-heading font-bold ${comparisonResult.income_comparison.change_amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {formatCurrency(comparisonResult.income_comparison.change_amount)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Tax Comparison */}
                          {comparisonResult.tax_comparison && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div className="p-3 bg-white rounded-sm border border-blue-100">
                                <p className="text-xs text-slate-500 font-body">Current Year Tax</p>
                                <p className="text-lg font-heading font-bold text-slate-900">
                                  {formatCurrency(comparisonResult.tax_comparison.current_year_tax)}
                                </p>
                              </div>
                              <div className="p-3 bg-white rounded-sm border border-blue-100">
                                <p className="text-xs text-slate-500 font-body">Prior Year Tax</p>
                                <p className="text-lg font-heading font-bold text-slate-900">
                                  {formatCurrency(comparisonResult.tax_comparison.prior_year_tax)}
                                </p>
                              </div>
                              <div className="p-3 bg-white rounded-sm border border-blue-100">
                                <p className="text-xs text-slate-500 font-body">Tax Change</p>
                                <p className={`text-lg font-heading font-bold ${comparisonResult.tax_comparison.change_amount <= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {formatCurrency(comparisonResult.tax_comparison.change_amount)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Key Differences */}
                          {comparisonResult.key_differences?.length > 0 && (
                            <div className="mt-4 p-4 bg-white rounded-sm border border-blue-100">
                              <p className="text-sm font-medium text-blue-800 font-body mb-2">Key Differences:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {comparisonResult.key_differences.map((diff, i) => (
                                  <li key={i} className="text-sm text-slate-600 font-body">{diff}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Recommendations */}
                          {comparisonResult.recommendations?.length > 0 && (
                            <div className="mt-4 p-4 bg-amber-50 rounded-sm border border-amber-200">
                              <p className="text-sm font-medium text-amber-800 font-body mb-2">Recommendations:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {comparisonResult.recommendations.map((rec, i) => (
                                  <li key={i} className="text-sm text-amber-700 font-body">{rec}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Summary */}
                          {comparisonResult.summary && (
                            <div className="mt-4 p-4 bg-slate-50 rounded-sm border border-slate-200">
                              <p className="text-sm text-slate-600 font-body whitespace-pre-wrap">{comparisonResult.summary}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : analysis?.status === "analyzing" ? (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-sm">
                <CardContent className="p-12 text-center">
                  <Loader2 className="w-16 h-16 text-slate-400 mx-auto mb-4 animate-spin" />
                  <p className="text-slate-600 font-body text-lg">Analyzing documents...</p>
                  <p className="text-slate-400 font-body text-sm mt-2">This may take a few moments</p>
                </CardContent>
              </Card>
            ) : analysis?.status === "error" ? (
              <Card className="bg-rose-50 border border-rose-200 shadow-sm rounded-sm">
                <CardContent className="p-12 text-center">
                  <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
                  <p className="text-rose-700 font-body text-lg">Analysis Failed</p>
                  <p className="text-rose-500 font-body text-sm mt-2">{analysis.overall_summary}</p>
                  <Button 
                    onClick={handleAnalyze}
                    className="mt-6 bg-rose-600 hover:bg-rose-700 text-white font-body rounded-md"
                  >
                    Try Again
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-sm">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-600 font-body text-lg">No Analysis Yet</p>
                  <p className="text-slate-400 font-body text-sm mt-2">
                    Upload tax documents and click "Analyze Documents" to get started
                  </p>
                  <Button 
                    onClick={() => setActiveTab("documents")}
                    className="mt-6 bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                  >
                    Go to Documents
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Saved Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-sm">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50 p-4 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading font-semibold text-slate-900">Saved Reports</CardTitle>
                  <p className="text-sm text-slate-500 font-body mt-1">Save and manage reports for future reference</p>
                </div>
                {analysis && (
                  <Button 
                    onClick={() => {
                      setReportName(`${client?.name || 'Client'} - Tax Summary ${analysis?.tax_year || selectedTaxYear}`);
                      setSaveReportDialogOpen(true);
                    }}
                    className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                    data-testid="save-new-report-btn"
                  >
                    <Files className="w-4 h-4 mr-2" />
                    Save Current Analysis
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-6">
                {savedReports.length === 0 ? (
                  <div className="text-center py-12">
                    <Files className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-600 font-body text-lg">No saved reports yet</p>
                    <p className="text-slate-400 font-body text-sm mt-2">
                      Run an analysis and save it to create a report for future reference
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedReports.map((report) => (
                      <div 
                        key={report.report_id}
                        className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`saved-report-${report.report_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-body font-medium text-slate-900">{report.name}</h4>
                            <div className="flex items-center gap-3 mt-1">
                              <Badge variant="outline" className="text-xs">Tax Year {report.tax_year}</Badge>
                              <span className="text-xs text-slate-500 font-body">
                                Saved by {report.created_by_name} on {new Date(report.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            {report.notes && (
                              <p className="text-xs text-slate-500 font-body mt-1 italic">{report.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            onClick={() => handleDownloadSavedReport(report)}
                            disabled={downloadingReport === report.report_id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-body rounded-md"
                            data-testid={`download-report-${report.report_id}`}
                          >
                            {downloadingReport === report.report_id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </>
                            )}
                          </Button>
                          <Button 
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSavedReport(report.report_id)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            title="Delete report"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Document Preview Dialog */}
        <Dialog open={!!previewDoc} onOpenChange={(open) => !open && closePreview()}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh]" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center justify-between">
                <span>{previewDoc?.filename}</span>
                <Button variant="ghost" size="sm" onClick={closePreview}>
                  <X className="w-4 h-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto max-h-[70vh] bg-slate-100 rounded-md">
              {previewUrl && previewDoc && (
                previewDoc.file_type === 'application/pdf' ? (
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="w-full h-[65vh] border border-slate-200 rounded-md"
                    title={previewDoc.filename}
                  >
                    <div className="flex flex-col items-center justify-center h-[65vh] text-slate-500">
                      <FileText className="w-16 h-16 mb-4 text-slate-300" />
                      <p className="text-lg font-body mb-2">PDF Preview Unavailable</p>
                      <p className="text-sm font-body mb-4">Your browser may not support inline PDF preview.</p>
                      <Button 
                        onClick={() => handleDownloadDocument(previewDoc)}
                        className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </object>
                ) : (
                  <img
                    src={previewUrl}
                    alt={previewDoc.filename}
                    className="max-w-full h-auto mx-auto"
                  />
                )
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline"
                onClick={() => handleDownloadDocument(previewDoc)}
                className="font-body rounded-md"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Notes Dialog */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="notes-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Document Notes</DialogTitle>
              <DialogDescription className="font-body text-slate-500">
                {selectedDocForNote?.filename}
              </DialogDescription>
            </DialogHeader>
            
            {/* Notes History */}
            {selectedDocForNote?.notes_history?.length > 0 && (
              <div className="flex-1 overflow-y-auto max-h-[300px] border border-slate-200 rounded-md p-3 bg-slate-50">
                <p className="text-xs text-slate-500 font-body uppercase tracking-wider mb-3">Note History</p>
                <div className="space-y-3">
                  {selectedDocForNote.notes_history.slice().reverse().map((note, idx) => (
                    <div key={note.note_id || idx} className="bg-white p-3 rounded-sm border border-slate-100">
                      <p className="text-sm text-slate-700 font-body whitespace-pre-wrap">{note.text}</p>
                      <p className="text-xs text-slate-500 font-body mt-2 flex items-center gap-1">
                        <span className="font-medium">{note.created_by_name}</span>
                        <span>•</span>
                        <span>{formatNoteTime(note.created_at)}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add New Note */}
            <div className="pt-4 border-t border-slate-200 mt-4">
              <Label className="text-sm font-medium text-slate-700 font-body mb-2 block">Add New Note</Label>
              <Textarea
                placeholder="Type your note here..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="font-body rounded-md min-h-[100px]"
                data-testid="note-textarea"
              />
            </div>
            
            <DialogFooter className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => setNoteDialogOpen(false)}
                className="font-body rounded-md"
              >
                Close
              </Button>
              <Button 
                onClick={handleSaveNote}
                disabled={savingNote || !noteText.trim()}
                className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                data-testid="save-note-btn"
              >
                {savingNote ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <StickyNote className="w-4 h-4 mr-2" />
                    Add Note
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-rose-600 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Delete Document
              </DialogTitle>
              <DialogDescription className="font-body text-slate-600 pt-2">
                Are you sure you want to delete <span className="font-medium text-slate-900">{documentToDelete?.filename}</span>?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDocumentToDelete(null);
                }}
                className="font-body rounded-md"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteDocument}
                disabled={deleting}
                className="bg-rose-600 hover:bg-rose-700 text-white font-body rounded-md"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Version Upload Dialog */}
        <Dialog open={versionUploadOpen} onOpenChange={setVersionUploadOpen}>
          <DialogContent className="sm:max-w-md" data-testid="version-upload-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                Upload New Version
              </DialogTitle>
              <DialogDescription className="font-body text-slate-600 pt-2">
                Upload a new version of <span className="font-medium text-slate-900">{selectedDocForVersion?.filename}</span>.
                The previous version will be preserved in history.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <input
                ref={versionFileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleVersionUpload}
                className="hidden"
                id="version-file-upload"
                data-testid="version-file-input"
              />
              <label htmlFor="version-file-upload">
                <div className="border-2 border-dashed border-blue-200 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors">
                  <Upload className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                  <p className="font-body font-medium text-slate-700">Click to select file</p>
                  <p className="text-sm text-slate-500 mt-1">PDF, JPG, or PNG</p>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setVersionUploadOpen(false);
                  setSelectedDocForVersion(null);
                }}
                className="font-body rounded-md"
                disabled={uploadingVersion}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Versions History Dialog */}
        <Dialog open={versionsDialogOpen} onOpenChange={setVersionsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh]" data-testid="versions-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Files className="w-5 h-5 text-blue-600" />
                Version History
              </DialogTitle>
              <DialogDescription className="font-body text-slate-600">
                {selectedDocForVersion?.filename}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 max-h-[50vh] overflow-y-auto">
              {loadingVersions ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                </div>
              ) : documentVersions.length > 0 ? (
                <div className="space-y-3">
                  {documentVersions.map((version) => (
                    <div 
                      key={version.document_id}
                      className={`p-4 rounded-lg border ${version.is_latest ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={version.is_latest ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'}>
                            v{version.version}
                          </Badge>
                          <div>
                            <p className="font-body font-medium text-slate-900">{version.filename}</p>
                            <p className="text-xs text-slate-500 font-body">
                              {new Date(version.uploaded_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {version.is_latest && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Current</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDocument(version)}
                            className="text-slate-600 hover:text-slate-900"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-8 text-slate-500">
                  <Files className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="font-body">No version history available</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline"
                onClick={() => {
                  setVersionsDialogOpen(false);
                  setSelectedDocForVersion(null);
                  setDocumentVersions([]);
                }}
                className="font-body rounded-md"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  setVersionsDialogOpen(false);
                  openVersionUpload(selectedDocForVersion);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-body rounded-md"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Upload New Version
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save Report Dialog */}
        <Dialog open={saveReportDialogOpen} onOpenChange={setSaveReportDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="save-report-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Files className="w-5 h-5 text-emerald-600" />
                Save Report
              </DialogTitle>
              <DialogDescription className="font-body text-slate-600 pt-2">
                Save this analysis report with a custom name for future reference.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="report-name" className="font-body text-sm font-medium">
                  Report Name <span className="text-rose-500">*</span>
                </Label>
                <Input
                  id="report-name"
                  placeholder="e.g., 2024 Final Tax Summary"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  className="font-body rounded-md"
                  data-testid="report-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-notes" className="font-body text-sm font-medium">Notes (Optional)</Label>
                <Textarea
                  id="report-notes"
                  placeholder="Add any notes about this report..."
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  className="font-body rounded-md min-h-[80px]"
                  data-testid="report-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSaveReportDialogOpen(false);
                  setReportName("");
                  setReportNotes("");
                }}
                className="font-body rounded-md"
                disabled={savingReport}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveReport}
                disabled={savingReport || !reportName.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-body rounded-md"
                data-testid="confirm-save-report-btn"
              >
                {savingReport ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Files className="w-4 h-4 mr-2" />
                    Save Report
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientDetailPage;
